/**
 * Admin dashboard home.
 *
 * Welcome message + quick action cards + live stats from /api/admin/stats.
 * Quick actions are placeholders for now (no click handlers wired) - they
 * point to future routes that will be built when the agency UX is ready.
 */
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  UserPlus,
  GraduationCap,
  Megaphone,
  Users,
  Envelope,
  ShieldStar,
} from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { adminApi } from "../../api/admin";

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-5"
  >
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl adonis-gradient">
      <Icon size={22} weight="light" className="text-white" />
    </span>
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
        {label}
      </p>
      <p className="mt-1 font-heading text-2xl font-bold tracking-tight text-slate-900">
        {value ?? "-"}
      </p>
    </div>
  </div>
);

const QuickAction = ({ icon: Icon, title, description, testId }) => (
  <button
    type="button"
    data-testid={testId}
    className="group text-left rounded-2xl border-2 border-slate-200 bg-white p-6 transition-all hover:border-slate-900 hover:-translate-y-0.5 hover:shadow-lg"
  >
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
      <Icon size={18} weight="bold" />
    </span>
    <h3 className="mt-4 font-heading text-base font-bold tracking-tight text-slate-900">
      {title}
    </h3>
    <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{description}</p>
  </button>
);

export default function AdminHome() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const data = await adminApi.stats();
      setStats(data);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (!user) return null;
  const firstName = user.first_name || user.email.split("@")[0];

  return (
    <div data-testid="admin-home" className="space-y-12">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("admin.home.kicker")}
        </p>
        <h1 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          {t("admin.home.welcome", { name: firstName })}
        </h1>
        <p className="mt-3 text-sm md:text-base text-slate-600 max-w-xl">
          {t("admin.home.subtitle")}
        </p>
      </header>

      {/* Quick actions */}
      <section data-testid="admin-quick-actions">
        <h2 className="font-heading text-lg md:text-xl font-bold tracking-tight text-slate-900 mb-5">
          {t("admin.home.quick_actions_title")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickAction
            testId="admin-action-add-student"
            icon={UserPlus}
            title={t("admin.home.action_add_student_title")}
            description={t("admin.home.action_add_student_desc")}
          />
          <QuickAction
            testId="admin-action-create-course"
            icon={GraduationCap}
            title={t("admin.home.action_create_course_title")}
            description={t("admin.home.action_create_course_desc")}
          />
          <QuickAction
            testId="admin-action-publish-announcement"
            icon={Megaphone}
            title={t("admin.home.action_publish_announcement_title")}
            description={t("admin.home.action_publish_announcement_desc")}
          />
        </div>
      </section>

      {/* Stats */}
      <section data-testid="admin-stats">
        <h2 className="font-heading text-lg md:text-xl font-bold tracking-tight text-slate-900 mb-5">
          {t("admin.home.stats_title")}
        </h2>
        {error ? (
          <p
            data-testid="admin-stats-error"
            className="text-sm text-red-600"
          >
            {t("admin.home.stats_error")}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              testId="admin-stat-students"
              icon={Users}
              label={t("admin.home.stat_students")}
              value={stats?.students}
            />
            <StatCard
              testId="admin-stat-admins"
              icon={ShieldStar}
              label={t("admin.home.stat_admins")}
              value={stats?.admins}
            />
            <StatCard
              testId="admin-stat-contact"
              icon={Envelope}
              label={t("admin.home.stat_contact_submissions")}
              value={stats?.contact_submissions}
            />
          </div>
        )}
      </section>
    </div>
  );
}
